import { JSX, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { SalesConversation } from '../../../types/entities';
import { DashboardCard } from './DashboardCard';
import { NewItemsBadge, dashboardListSx } from './ListWidgetChrome';
import { loadAwaitingReply } from '../../edit/components/Sales/salesConversationApi';

/**
 * Contact form and enterprise request conversations where they have written and nobody has
 * answered yet.
 *
 * **Full width rather than a third half-width card, deliberately.** HQ-09: the two "needs
 * attention" cards already scroll sideways below about 1600px because seven columns will not fit
 * a 527px card, and adding a third to that row would make a known layout problem worse. A full
 * row has the space for five columns without pushing anything off screen.
 *
 * This is a different question from the two cards above it, which list submissions nobody has
 * picked up. A conversation reaching this card has already been worked at least once and is now
 * waiting on us.
 */
export const ConversationsAwaitingReplyData: () => JSX.Element = (): JSX.Element => {
    const [rows, setRows] = useState<SalesConversation[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string|null>(null);

    useEffect((): (() => void) => {
        let live: boolean = true;
        void (async (): Promise<void> => {
            try {
                const loaded: SalesConversation[] = await loadAwaitingReply(10);
                if (live) {
                    setRows(loaded);
                }
            } catch (caught) {
                if (live) {
                    setError(caught instanceof Error ? caught.message : 'Could not load the conversations.');
                }
            } finally {
                if (live) {
                    setLoading(false);
                }
            }
        })();

        return (): void => { live = false; };
    }, []);

    /** The HQ route to the record, landing on its Conversation tab. */
    const routeFor = (conversation: SalesConversation): string|null => {
        if (conversation.subjectId == null) {
            return null;
        }
        const resource: string = conversation.subjectType === 'enterprise_request'
            ? 'enterprise_requests'
            : 'contact_forms';

        // `/1` is the tab index, so the link lands on the conversation rather than the enquiry.
        return `/${ resource }/${ conversation.subjectId }/1`;
    };

    return (
        <DashboardCard
            eyebrow="Needs attention"
            title="Conversations awaiting a reply"
            minHeight={ 280 }
            busy={ loading }
            actions={ loading || error !== null ? undefined : <NewItemsBadge total={ rows.length } /> }
        >
            <Box sx={ dashboardListSx }>
                { error !== null && (
                    <Typography variant="body2" color="error">{ error }</Typography>
                ) }
                { error === null && !loading && rows.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                        { 'Nobody is waiting on a reply.' }
                    </Typography>
                ) }
                { rows.length > 0 && (
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell className="RaDatagrid-headerCell">{ 'From' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell">{ 'Type' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell">{ 'Assigned to' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell">{ 'Last message' }</TableCell>
                                <TableCell className="RaDatagrid-headerCell" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            { rows.map((conversation: SalesConversation): JSX.Element => {
                                const route: string|null = routeFor(conversation);

                                return (
                                    <TableRow key={ conversation.id }>
                                        <TableCell className="RaDatagrid-rowCell">
                                            { conversation.recipientName || 'Unknown' }
                                        </TableCell>
                                        <TableCell className="RaDatagrid-rowCell">
                                            { conversation.subjectType === 'enterprise_request'
                                                ? 'Enterprise'
                                                : 'Contact form' }
                                        </TableCell>
                                        <TableCell className="RaDatagrid-rowCell">
                                            { /* Unassigned is the one worth spotting: nobody has
                                                 picked this thread up. */ }
                                            <Chip
                                                size="small"
                                                variant={ conversation.assignedTo ? 'filled' : 'outlined' }
                                                color={ conversation.assignedTo ? 'primary' : 'default' }
                                                label={ conversation.assignedTo?.name ?? 'Unassigned' }
                                            />
                                        </TableCell>
                                        <TableCell className="RaDatagrid-rowCell">
                                            { conversation.lastMessageAt
                                                ? new Date(conversation.lastMessageAt).toLocaleDateString('en-GB')
                                                : '' }
                                        </TableCell>
                                        <TableCell className="RaDatagrid-rowCell">
                                            { route !== null && (
                                                <RouterLink to={ route }>{ 'Open' }</RouterLink>
                                            ) }
                                        </TableCell>
                                    </TableRow>
                                );
                            }) }
                        </TableBody>
                    </Table>
                ) }
            </Box>
        </DashboardCard>
    );
};
